import { pluralize } from "@common/helpers/string";
import { DocumentMigrationErrorCode } from "@common/constants/accounting-migration";
import { DocumentMigrationIssueDto } from "@common/schemas/accounting-integration.schema";
import { ImportRejection } from "@common/schemas/domains/accounting/import-rejection.schema";
import {
    MigrationMatchedRow,
    MigrationStatusText,
} from "./migrationFileMatching";

interface MigrationIssueOverlayRow {
    documentId?: number | null;
    status: string;
    statusMessage?: MigrationStatusText;
}

const MIGRATION_VALIDATION_SKIP_STATUSES = new Set([
    "extracting",
    "unrecognized",
    "basis_required",
]);

export const HAS_ISSUES_MESSAGE: MigrationStatusText = {
    short: "Has issues",
    tooltip: "Resolve before completing",
};

// Per-issue cell text: a terse label plus a fuller hover explanation that
// points at the file the user must fix.
function singleIssueStatusText(
    issue: DocumentMigrationIssueDto,
): MigrationStatusText {
    switch (issue.code) {
        case DocumentMigrationErrorCode.ACCOUNT_CODE_MISMATCH: {
            const count = issue.missingCodes?.length ?? 0;
            return {
                short: "Missing codes",
                tooltip: `${count} ${pluralize(
                    "code",
                    count,
                )} not in this chart`,
            };
        }
        case DocumentMigrationErrorCode.UNBALANCED_JOURNAL_ENTRIES: {
            const count = issue.unbalancedDays?.length ?? 0;
            return {
                short: "Unbalanced",
                tooltip: `${count} ${pluralize("day", count)} ${
                    count === 1 ? "doesn't" : "don't"
                } balance`,
            };
        }
        case DocumentMigrationErrorCode.OPENING_BALANCES_UNRECONCILED:
            return {
                short: "Unreconciled",
                tooltip:
                    "Scanned opening balances should net to 0. " +
                    "Unsupported export",
            };
        default:
            return HAS_ISSUES_MESSAGE;
    }
}

// A single document can carry more than one blocking issue; the row shows one
// status, so a single issue keeps its specific text while multiple are combined.
function migrationIssuesStatusText(
    issues: DocumentMigrationIssueDto[],
): MigrationStatusText {
    if (issues.length === 1) {
        return singleIssueStatusText(issues[0]);
    }
    return {
        short: HAS_ISSUES_MESSAGE.short,
        tooltip: issues
            .map((issue) => singleIssueStatusText(issue).tooltip)
            .join("; "),
    };
}

function groupIssuesByDocumentId(
    issues: DocumentMigrationIssueDto[],
): Map<number, DocumentMigrationIssueDto[]> {
    const issuesByDocumentId = new Map<number, DocumentMigrationIssueDto[]>();
    for (const issue of issues) {
        if (issue.documentId == null) {
            continue;
        }
        const existing = issuesByDocumentId.get(issue.documentId) ?? [];
        existing.push(issue);
        issuesByDocumentId.set(issue.documentId, existing);
    }
    return issuesByDocumentId;
}

function resolveRowWithIssues<T extends MigrationIssueOverlayRow>(
    row: T,
    documentIssues: DocumentMigrationIssueDto[],
    skipStatuses: ReadonlySet<string>,
): T {
    if (skipStatuses.has(row.status)) {
        return row;
    }
    if (documentIssues.length === 0) {
        return row;
    }
    return {
        ...row,
        status: "has_issues",
        statusMessage: migrationIssuesStatusText(documentIssues),
    };
}

/**
 * Overlays each blocking validation issue onto the upload row of the document
 * it belongs to, so accounting-logic problems are flagged in-grid rather than
 * in a separate banner.
 */
export function applyMigrationIssues<T extends MigrationIssueOverlayRow>({
    rows,
    issues,
    skipStatuses = MIGRATION_VALIDATION_SKIP_STATUSES,
}: {
    rows: T[];
    issues: DocumentMigrationIssueDto[];
    skipStatuses?: ReadonlySet<string>;
}): T[] {
    if (issues.length === 0) {
        return rows;
    }

    const issuesByDocumentId = groupIssuesByDocumentId(issues);

    return rows.map((row) =>
        resolveRowWithIssues(
            row,
            row.documentId != null
                ? (issuesByDocumentId.get(row.documentId) ?? [])
                : [],
            skipStatuses,
        ),
    );
}

export function applyCoaCrossFileRejection({
    rows,
    rejection,
}: {
    rows: MigrationMatchedRow[];
    rejection?: ImportRejection;
}): MigrationMatchedRow[] {
    if (!rejection) {
        return rows;
    }

    return rows.map((row) => {
        if (
            row.role !== "coa" ||
            row.documentId == null ||
            row.status === "extracting" ||
            row.status === "unrecognized" ||
            row.status === "basis_required"
        ) {
            return row;
        }
        return {
            ...row,
            status: "has_issues",
            statusMessage: {
                short: rejection.summary,
                tooltip: rejection.guidance,
            },
            rejection,
        };
    });
}
